import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { ChevronDown, ChevronUp, Printer, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useListPharmacistCounsellingChecklists } from "@workspace/api-client-react";
import type { PharmacistCounsellingChecklist } from "@workspace/api-client-react";

const categoryColours: Record<string, string> = {
  "DMARD": "bg-teal-50 text-teal-700 border-teal-200",
  "Biologic": "bg-violet-50 text-violet-700 border-violet-200",
  "JAK Inhibitor": "bg-amber-50 text-amber-700 border-amber-200",
};

function DrugCounsellingCard({ drug }: { drug: PharmacistCounsellingChecklist }) {
  const [expanded, setExpanded] = useState(false);

  const handlePrint = () => {
    const printContent = `
      <h2>${drug.drug} — Patient Counselling Summary</h2>
      <p><strong>Class:</strong> ${drug.drugClass}</p>
      <ul>${drug.patientSummaryPoints.map((p) => `<li>${p}</li>`).join("")}</ul>
      <p style="margin-top:1em;font-size:0.85em;color:#666">Generated from RheumHub — For pharmacist/nurse reference only. Always individualise counselling.</p>
    `;
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(`<html><body style="font-family:sans-serif;max-width:600px;margin:2em auto;">${printContent}</body></html>`);
      win.document.close();
      win.print();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-lg font-bold text-slate-900">{drug.drug}</h3>
            <Badge className={`border text-xs ${categoryColours[drug.category] ?? ""}`}>{drug.category}</Badge>
            <span className="text-xs text-slate-500">{drug.drugClass}</span>
          </div>
          <p className="text-sm text-slate-600"><span className="font-medium">Key counselling:</span> {drug.keyPoints}</p>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-slate-100 pt-4">
          {drug.sections.map((section) => (
            <div key={section.heading}>
              <h4 className="text-sm font-bold text-slate-800 mb-3">{section.heading}</h4>
              <ul className="space-y-2">
                {section.content.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-violet-400 mt-0.5 shrink-0">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-violet-900">Patient Summary Card</h4>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs text-violet-700 hover:text-violet-900 bg-white border border-violet-300 px-3 py-1.5 rounded-lg"
              >
                <Printer className="h-3.5 w-3.5" />
                Print summary
              </button>
            </div>
            <ul className="space-y-1.5">
              {drug.patientSummaryPoints.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-violet-900">
                  <span className="text-violet-500 font-bold shrink-0">✓</span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PharmacistCounselling() {
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useListPharmacistCounsellingChecklists();

  const checklists = data ?? [];
  const filtered = checklists.filter(
    (d) =>
      search === "" ||
      d.drug.toLowerCase().includes(search.toLowerCase()) ||
      d.drugClass.toLowerCase().includes(search.toLowerCase()) ||
      d.keyPoints.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PharmacistLayout title="Patient Counselling">
      <div className="space-y-6">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm text-violet-800">
          <strong>Pharmacist counselling frameworks.</strong> Structured initiation counselling guides for key rheumatology medicines. Always individualise counselling to the patient. Patient summaries can be printed for hand-out. Based on SPC, BNF, BSR, and NHSE guidance.
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search drug or class..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="text-sm text-slate-500 px-1">Loading counselling checklists…</div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            Failed to load counselling checklists. Please try again.
          </div>
        )}

        <div className="space-y-4">
          {filtered.map((drug) => (
            <DrugCounsellingCard key={drug.id} drug={drug} />
          ))}
          {!isLoading && !error && filtered.length === 0 && (
            <div className="text-sm text-slate-500 px-1">No checklists match your search.</div>
          )}
        </div>
      </div>
    </PharmacistLayout>
  );
}
